import {
  Box,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  updateTenetDetails,
  updateTenetDetailsLogo,
  updateTenetStatus,
} from "../../../../api/tenets";
import { AlertMessage } from "../tenant-details-types";
import moment from "moment";
import {
  confirmationDefaultValues,
  ConfirmationPopupTypes,
} from "../../tenant-list/tenant-list-helper";
import EditBasicAddress from "../tenant-details-right-panel/tenant-basic-details/edit-basic-address/edit-basic-address";
import EqualWidthList from "../../../../common/components/equal-width-list/EqualWidthList";
import ColorthemeInputComponent from "../../../../common/components/colortheme-input-component/colortheme-input-component";
import _ from "lodash";
import ConfirmationPopup from "../../../../common/components/confirmation-popup/confirmation-popup";
import icons from "../../../../assets/icons/icons";
import { AlertComponent } from "../../../../common/components";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { defaultColors } from "../../../../utils/themeUtils";
import ContactLink from "../../../../common/components/contact-link/contact-link";
import { useResourcePermissions } from "../../../../common/hooks/resource-permissions";
import MODULES from "../../../../constants/modules";
import { convertUTCToLocal, isValidPhoneNumber } from "../../../../utils/utils";

interface TenantDetailsLeftPanelProps {
  tenantData: any;
  tenetRoles: any;
  setTenantData: any;
  partnerDetails: any;
  tenantId: any;
}

const options = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const FILE_SIZE = 110 * 1024 * 1024;

const TenantDetailsLeftPanel: React.FC<TenantDetailsLeftPanelProps> = ({
  tenantData,
  tenetRoles,
  setTenantData,
  partnerDetails,
  tenantId,
}) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [uploadedImage, setUploadedImage] = React.useState<any>({});
  const [isCompanyAddressEditing, setIsCompanyAddressEditing] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(defaultColors.primaryColor);
  const [accentColor, setAccentColor] = useState(defaultColors.accentColor);
  const [alertMessage, setAlertMessage] = useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });
  const [confirmationModal, setConfirmationModal] =
    useState<ConfirmationPopupTypes>(confirmationDefaultValues);
  const { canWrite } = useResourcePermissions(MODULES.PARTNER_LIST);

  const companyAddressFormRef = useRef<any>(null);

  const handleError = (err: any, msg?: string) => {
    setAlertMessage({
      open: true,
      message: (err as any)?.response?.data?.error || msg,
      type: "error",
    });
  };

  const handleApiSuccuss = (successMsg: string) => {
    setAlertMessage({
      open: true,
      message: successMsg,
      type: "success",
    });
  };

  const handleSaveTheme = async () => {
    const payload = {
      primaryColor: primaryColor,
      accentColor: accentColor,
    };

    try {
      setIsLoading(true);
      const response = await updateTenetDetails(tenantId, "theme", payload);
      handleApiSuccuss("Theme updated successfully");
      setTenantData((prevData: any) => ({
        ...prevData,
        theme: {
          ...prevData?.theme,
          primary_color: primaryColor,
          accent_color: accentColor,
        },
      }));
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.error("Failed to update theme", error);
      handleError(error, "Failed to update theme");
    }
  };

  const handleCompanyAddressAPISuccess = (res: any) => {
    setTenantData((prev: any) => ({
      ...prev,
      company_address: res.company_address,
    }));
    handleApiSuccuss("Address updated successfully");
    setIsCompanyAddressEditing(false);
  };

  const onHandleChange = (value: string) => {
    const isActive = value === "ACTIVE";
    const statusText = isActive ? "Active" : "Inactive";
    const noteText = isActive
      ? "Active will allow the partner user to use the application."
      : "Inactive will prevent the partner user from logging in to the application.";

    setConfirmationModal({
      ...confirmationDefaultValues,
      isOpen: true,
      title: `Are you sure you want to make the partner ${statusText}?`,
      partnerDetails: partnerDetails,
      note: `Note: Changing the status to ${noteText}`,
      value: value,
    });
  };

  const handleImageUploade = async (file: any) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append(
        "companyName",
        tenantData?.basic_details?.company_name || ""
      );
      const response = await updateTenetDetailsLogo(tenantId, formData);
      setTimeout(() => {
        setTenantData((prev: any) => ({
          ...prev,
          basic_details: {
            ...prev.basic_details,
            logo_url: response?.data?.data?.logo_url
              ? `${response.data.data.logo_url}?t=${Date.now()}`
              : prev.basic_details.thumbnail_two_url,
          },
        }));
      }, 2000);

      setIsLoading(false);
      handleApiSuccuss("Logo updated successfully");
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.error("Failed to update logo", error);
      handleError(error, "Failed to update logo");
    }
  };

  const handleProfileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file?.size > FILE_SIZE) {
        handleError({}, "File size must be less than 100MB");
        return;
      }
      handleImageUploade(file);
      setUploadedImage((prev: any) => ({
        ...prev,
        error: "",
        file: file,
        urlImg: URL.createObjectURL(file),
      }));
    }
  };

  const handlePartnerStatus = async (partnerId: number, payload: any) => {
    try {
      setIsLoading(true);
      const res = await updateTenetStatus(partnerId, payload);
      setTenantData((prev: any) => ({
        ...prev,
        basic_details: {
          ...prev.basic_details,
          status: confirmationModal.value,
        },
      }));
      setIsLoading(false);

      setConfirmationModal(confirmationDefaultValues);
      handleApiSuccuss("Partner status updated successfully.");
    } catch (err: any) {
      setIsLoading(false);

      handleError(err, "Failed to update status.");
    }
  };

  const handleOnOkay = () => {
    const payload = { status: confirmationModal?.value };
    const partnerId = confirmationModal?.partnerDetails?.id;
    handlePartnerStatus(partnerId, payload);
  };

  const handleCancel = () => {
    setConfirmationModal(confirmationDefaultValues);
  };

  useEffect(() => {
    if (tenantData?.theme) {
      setPrimaryColor(
        tenantData?.theme?.primary_color || defaultColors.primaryColor
      );
      setAccentColor(
        tenantData?.theme?.accent_color || defaultColors.accentColor
      );
    }
  }, [tenantData]);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <Box className="tenant-details-left-panel">
      <Box className="tenant-details-left-panel-profile">
        <Box className="tenant-details-left-panel-profile-image">
          <img
            src={uploadedImage?.urlImg || tenantData?.basic_details?.logo_url}
            alt="profile"
          />

          <div
            className="edit-overlay"
            onClick={() => {
              if (canWrite) document.getElementById("upload-input")?.click();
            }}
          >
            <ModeEditOutlinedIcon className="edit-icon" />
          </div>
          <input
            type="file"
            id="upload-input"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleProfileUpload}
          />
        </Box>
        <Box className="tenant-details-left-panel-profile-info">
          <span className="tenant-details-left-panel-profile-header">
            {tenantData?.basic_details?.company_name}
          </span>
          <p>{tenantData?.basic_details?.email}</p>
          <Box>
            <ContactLink
              value={
                isValidPhoneNumber(tenantData?.basic_details?.phone_number)
                  ? tenantData?.basic_details?.phone_number
                  : "-"
              }
              type="Phone"
            />
          </Box>
          <p>
            Onboarded On:{" "}
            {tenantData?.basic_details?.created_at
              ? convertUTCToLocal(partnerDetails?.created_at, "MM/DD/YYYY")
              : "-"}
          </p>

          <Box
            className="tenant-details-left-panel-profile-status"
            sx={{
              border: `1px solid ${tenantData?.basic_details?.status === "ACTIVE" ? "green" : "red"} `,
            }}
          >
            {" "}
            <Select
              value={tenantData?.basic_details?.status || ""}
              onChange={(event: SelectChangeEvent<string>) => {
                onHandleChange(event.target.value);
              }}
              displayEmpty={true}
              disabled={!canWrite}
              IconComponent={(props) => (
                <ExpandMoreIcon
                  {...props}
                  sx={{
                    color:
                      tenantData?.basic_details?.status === "ACTIVE"
                        ? "green !important"
                        : "red !important",
                  }}
                  fontSize="small"
                />
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    width: "135px",
                  },
                },
              }}
              sx={{
                color:
                  tenantData?.basic_details?.status === "ACTIVE"
                    ? "green"
                    : "red",
              }}
              className="Mui-table-select-dropdown tenant-details-select-dropdown"
            >
              {options.map((option: any) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  style={{
                    color: option.value === "ACTIVE" ? "green" : "red",
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </Box>

      {/* address card */}
      <Box className="tenant-details-left-panel-address">
        <Box
          className="tenant-details-left-panel-address-header"
          sx={{ minHeight: "30px" }}
        >
          <p>Company address</p>
          <Box>
            {isCompanyAddressEditing ? (
              <Box sx={{ display: "flex", marginRight: "-8px" }}>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => {
                    setIsCompanyAddressEditing(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    companyAddressFormRef.current?.submitForm();
                  }}
                  className="cancel-button next-button-wrapper"
                  //   disabled={!(isFormDirty || uploadedImageFile)}
                >
                  Save
                </button>
              </Box>
            ) : (
              <>
                {canWrite ? (
                  <Button
                    variant="outlined"
                    className="address-edit-button"
                    onClick={() => setIsCompanyAddressEditing(true)}
                  >
                    {isCompanyAddressEditing ? "Save" : "Edit"}
                  </Button>
                ) : (
                  <div></div>
                )}
              </>
            )}
          </Box>
        </Box>
        {isCompanyAddressEditing ? (
          <Box className="tenant-details-left-panel-address-edit">
            <EditBasicAddress
              data={tenantData}
              formRef={companyAddressFormRef}
              type="company"
              handleSuccessApi={handleCompanyAddressAPISuccess}
              initialValues={{
                ...tenantData?.company_address,
                streetLine1: tenantData?.company_address?.street_line_1 || "",
                streetLine2: tenantData?.company_address?.street_line_2 || "",
              }}
              handleError={handleError}
            />
          </Box>
        ) : (
          <Box className="tenant-details-left-panel-address-info">
            <EqualWidthList
              label="Street Line 1"
              labelValue={tenantData?.company_address?.street_line_1 || "-"}
            />
            <EqualWidthList
              label="Street Line 2"
              labelValue={tenantData?.company_address?.street_line_2 || "-"}
            />
            <EqualWidthList
              label="State"
              labelValue={tenantData?.company_address?.state || "-"}
            />
            <EqualWidthList
              label="Country"
              labelValue={tenantData?.company_address?.country || "-"}
            />
            <EqualWidthList
              label="City"
              labelValue={tenantData?.company_address?.city || "-"}
            />
            <EqualWidthList
              label="Zip Code"
              labelValue={tenantData?.company_address?.postcode || "-"}
            />
          </Box>
        )}
      </Box>
      <Box className="tenant-details-left-panel-plan">
        <Box className="tenant-details-left-panel-plan-header">
          <span className="plan-header-text">Plan</span>
          <Box className="plan-button-wrapperr">
            {tenantData?.purchased_subscription_plan?.plan_name || "-"}
          </Box>
        </Box>
        <Box className="tenant-details-left-panel-plan-header">
          <span className="plan-header-text">Users Added</span>
          <span className="plan-header-users">
            {tenantData?.users?.length?.toString().padStart(2, "0")}/
            {Number(tenantData?.purchased_subscription_plan?.no_of_users) === -1
              ? "Unlimited"
              : tenantData?.purchased_subscription_plan?.no_of_users
                  ?.toString()
                  .padStart(2, "0")}
          </span>
        </Box>
      </Box>
      <Box className="tenant-details-left-panel-theme">
        <Box className="tenant-details-left-panel-theme-header">
          <p>Theme</p>

          {canWrite && (
            <Button
              variant="outlined"
              className="address-edit-button"
              onClick={handleSaveTheme}
              sx={{
                "&.Mui-disabled": {
                  cursor: "not-allowed",
                  pointerEvents: "auto",
                },
              }}
              disabled={
                _.isEqual(tenantData?.theme?.primary_color, primaryColor) &&
                _.isEqual(tenantData?.theme?.accent_color, accentColor)
              }
            >
              Save
            </Button>
          )}
        </Box>
        <Box className="tenant-details-left-panel-theme-info">
          <ColorthemeInputComponent
            value={primaryColor}
            handleColorChange={(e: any) => {
              if (canWrite) setPrimaryColor(e.target.value);
            }}
            name="primary"
            label="Primary Color"
          />
          <ColorthemeInputComponent
            value={accentColor}
            handleColorChange={(e: any) => {
              if (canWrite) setAccentColor(e.target.value);
            }}
            name="accent"
            label="Accent Color"
          />
        </Box>
      </Box>

      {confirmationModal?.isOpen && (
        <ConfirmationPopup
          open={confirmationModal?.isOpen}
          title={confirmationModal?.title}
          note={confirmationModal?.note}
          buttonText={confirmationModal?.button}
          onCancel={handleCancel}
          onOkay={handleOnOkay}
          className="partner-list-confirmation"
          Icon={icons.AlertIcon}
        />
      )}
      {alertMessage.open && (
        <AlertComponent
          message={alertMessage.message}
          type={alertMessage.type || "info"}
          open={alertMessage.open}
          onClose={() => setAlertMessage({ open: false })}
        />
      )}
    </Box>
  );
};

export default TenantDetailsLeftPanel;
